import React from 'react';
import styled from 'styled-components';
import { Header } from '@app/navigation/header';
import { Body, FooterSeparator } from '@ui';
import { useNavigate } from 'react-router';
import { isUndefinedOrNull, logger, logNetRequest, scrollToTop } from '@utils/usefulFuncs';
import { Body5, Container, H5, Page } from '@ui';
import { addAdvancedSettingValues, useAdvancedSettings } from '@hooks/application/useAdvancedSettings';
import { AI_TOOLS, DOC_TYPE_14A } from '@constants';
import BackArrowButton from '@ui/buttons/BackArrowButton';
import { Spinner } from '@ui/LoadingSpinner';
import { AdvancedSettingsButton } from '@containers/DigitalAnalyst/AdvancedSettings';
import { CompanySelectionInput } from '@containers/DigitalAnalyst/components/CompanySelectionInput';
import { FormState } from '@ui/FormState';
import { FlatButton } from '@ui/buttons';
import { SkeletonButton } from '@ui/buttons/SkeletonButton';
import { ROUTES } from '@constants/routes';
import { getResultId } from '@hooks/features/useAskDigitalAnalyst';
import { QuestionResult } from '@containers/DigitalAnalyst/components/QuestionResult';
import { createTimeInstance } from '@utils/time';
import { fetchCompanyDocs } from '@hooks/features/useDocsCache';
import { submitQueriesStream } from '@services/genaiQueriesV2';

const PageWrapper = styled.div`
  + div {
    width: 100%;
  }
`;
const SubmitButtonWrapper = styled.div`
  text-align: center;
  width: 100%;
`;
const FormWrapper = styled.div`
  input {
    border: 1px solid ${({ theme }) => theme.themeColors.border};
    color: ${({ theme }) => theme.themeColors.text};
    background-color: transparent;
    border-radius: 6px;
    width: 100%;
    padding: 0 !important;
  }

  select {
    border: 1px solid ${({ theme }) => theme.themeColors.border};
    color: ${({ theme }) => theme.themeColors.text};
    background-color: transparent;
    border-radius: 6px;
    width: 100%;
    padding: 12px 16px;
    -webkit-appearance: auto !important;
  }

  textarea {
    border: 1px solid ${({ theme }) => theme.themeColors.border};
    color: ${({ theme }) => theme.themeColors.text};
    background-color: transparent;
    border-radius: 6px;
    min-height: 100px;
    width: 100%;
    padding: 16px;
  }
`;

const tool = AI_TOOLS.ESG_UPDATES;

const WORKING_STATES = {
  gettingDocs: 'gd',
  gettingResults: 'gr',
};

const SUMMARY_PROMPT = "Summarize the company's ESG policies in detail. Break each section out.";
const SUMMARY_SECTION_DISPLAY_QUESTION = 'ESG Policy Summary (last 12 months)';
const COMPARISON_QUESTION =
  "Compare the commentary and disclosures related to ESG from the company's filings in the last 12 months, to those from the previous 12 months";

const COMPARISON_SECTION_DISPLAY_QUESTION = 'ESG Policy Comparison (last 2 years)';

const getComparisonAnswer = async (docList, advancedSettingsValues) => {
  const settings = { user_mode: tool };
  addAdvancedSettingValues(advancedSettingsValues, settings);

  const result = await submitQueriesStream({
    questions: COMPARISON_QUESTION,
    docIds: docList,
    settings,
  });

  if (result.status === 200) {
    return {
      success: true,
      resultId: result.runId,
    };
  } else {
    return {
      error: 'Something went wrong, please try again.',
    };
  }
};

function fetchFilteredDocs(company) {
  const filters = { doc_types: [...DOC_TYPE_14A, '10-K', '10-Q'].join(',') };
  return fetchCompanyDocs(company, filters);
}

function ESGUpdatesTool({ onGoBack }) {
  const advancedSettingsValues = useAdvancedSettings();
  const { model } = advancedSettingsValues;

  const [company, setCompany] = React.useState(null);
  const [workingState, setWorkingState] = React.useState(null);
  const [formError, setFormError] = React.useState(null);

  const [summaryResultId, setSummaryResultId] = React.useState(null);
  const [comparisonResultId, setComparisonResultId] = React.useState(null);

  React.useEffect(() => {
    if (summaryResultId) {
      const id = 'snapshot-answers-section';
      const yOffset = -26;
      const element = document.getElementById(id);
      if (element) {
        const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;

        setTimeout(() => {
          window.scrollTo({ top: y, behavior: 'smooth' });
        }, 300);
      }
    }
  }, [summaryResultId]);

  const onSubmit = async () => {
    setWorkingState(WORKING_STATES.gettingDocs);
    setFormError(null);

    const docs = await fetchFilteredDocs(company);
    if (!docs) {
      setFormError('Something went wrong, please try again.');
      setWorkingState(null);
      return;
    }
    if (docs?.length === 0) {
      setFormError('There are no current available ESG documents for this company.');
      setWorkingState(null);
      return;
    }
    setWorkingState(WORKING_STATES.gettingResults);

    const oneYearAgo = createTimeInstance().subtract(1, 'year');
    const twoYearAgo = createTimeInstance().subtract(2, 'year');
    const last12MonthsDocs = docs.filter(
      (d) => !isUndefinedOrNull(d.report_date) && createTimeInstance(d.report_date).isAfter(oneYearAgo)
    );
    const last12to24MonthsDocs = docs.filter(
      (d) =>
        !isUndefinedOrNull(d.report_date) &&
        createTimeInstance(d.report_date).isBefore(oneYearAgo) &&
        createTimeInstance(d.report_date).isAfter(twoYearAgo)
    );

    if (last12MonthsDocs.length === 0 || last12to24MonthsDocs.length === 0) {
      setFormError('There are not enough current available ESG documents for this company to run this report.');
      setWorkingState(null);
      return;
    }

    logger(
      'reports used',
      { last12MonthsDocs: last12MonthsDocs.map((d) => d.report_date) },
      { last12to24MonthsDocs: last12to24MonthsDocs.map((d) => d.report_date) }
    );

    const params = {
      selectedDocs: last12MonthsDocs,
      screen_mode: false,

      tool,

      model,
      enableFollowUpQuestions: false,
    };
    const { id: resultId } = await getResultId(SUMMARY_PROMPT, params, {});
    if (resultId) {
      setSummaryResultId(resultId);
      setWorkingState(null);
    } else {
      setFormError('Something went wrong getting the summary answer, please refresh and try again');
    }

    const comparisonDocList = [...last12MonthsDocs.map((d) => d.id), ...last12to24MonthsDocs.map((d) => d.id)];
    const { resultId: comparisonResultId } = await getComparisonAnswer(comparisonDocList, advancedSettingsValues);
    if (comparisonResultId) {
      setComparisonResultId(comparisonResultId);
    } else {
      setFormError('Something went wrong getting the comparison answer, please refresh and try again');
    }
  };

  const onClear = () => {
    setCompany(null);
    setSummaryResultId(null);
    setComparisonResultId(null);
    setFormError(null);
  };

  const onAddTicker = (t) => {
    setCompany(t);
  };

  const onRemoveTicker = () => {
    setCompany(null);
  };

  const handleClearSnapshot = () => {
    onClear();
  };

  return (
    <PageWrapper>
      <Page width={'1200px'}>
        <BackArrowButton onClick={onGoBack} />
        <Container top={40}>
          <H5>ESG Updates</H5>
        </Container>
        {workingState === WORKING_STATES.gettingDocs ? (
          <Container top={80} bottom={80} centerAll>
            <Container>
              <Container centerAll>
                <Spinner />
              </Container>
              <Container top={24} left={12}>
                <Body5>Working...</Body5>
              </Container>
            </Container>
          </Container>
        ) : (
          <Container top={8}>
            <FormWrapper>
              <AdvancedSettingsButton tool={tool} />
              <Container top={24}>
                <Container bottom={8}>
                  <Body5>Select Company / Tag</Body5>
                </Container>
                <CompanySelectionInput
                  value={company}
                  label={null}
                  onAddCompany={onAddTicker}
                  onRemoveCompany={onRemoveTicker}
                  placeholder={'Search Companies / Tags'}
                  alwaysHideClear
                />
              </Container>
              <Container top={24}>
                <Container centerAll>
                  <FormState error={formError} />
                </Container>
                <SubmitButtonWrapper>
                  <FlatButton disabled={company === null} onClick={onSubmit} fullWidth>
                    Get Updates
                  </FlatButton>
                </SubmitButtonWrapper>
              </Container>
            </FormWrapper>
          </Container>
        )}
      </Page>
      {(workingState === WORKING_STATES.gettingResults || summaryResultId) && (
        <Page width={'1200px'}>
          <div id={'snapshot-answers-section'} />
          <Container top={16}>
            <Container bottom={4}>
              <H5>{SUMMARY_SECTION_DISPLAY_QUESTION}</H5>
            </Container>
            <QuestionResult
              isSubmittingToApi={false}
              resultId={summaryResultId}
              userMode={tool}
              question={null}
              enableQualityCheck
              enableFollowUpQuestions={false}
              collapseHeight
            />
          </Container>
          <Container top={24} />
          <FooterSeparator />
          <Container top={16}>
            <Container bottom={4}>
              <H5>{COMPARISON_SECTION_DISPLAY_QUESTION}</H5>
            </Container>
            <Container>
              <QuestionResult
                isSubmittingToApi={!comparisonResultId}
                resultId={comparisonResultId}
                userMode={tool}
                question={null}
                enableFollowUpQuestions={false}
                collapseHeight
              />
            </Container>
          </Container>
          {workingState === null && (
            <Container top={24} centerAll>
              <SkeletonButton onClick={handleClearSnapshot}>Close</SkeletonButton>
            </Container>
          )}
        </Page>
      )}
    </PageWrapper>
  );
}

function ESGUpdatesPage() {
  const navigate = useNavigate();

  const onGoBack = () => navigate(ROUTES.home);

  React.useEffect(() => {
    scrollToTop();
  }, []);

  return (
    <PageWrapper>
      <Header />
      <Body withFooter>
        <ESGUpdatesTool onGoBack={onGoBack} />
      </Body>
    </PageWrapper>
  );
}

export default ESGUpdatesPage;
